import { ReactElement, useMemo, useState } from 'react';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Box, Divider, Popover, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ReactComponent as SlackIcon } from '../../../assets/16x16/slack.svg';
import { ReactComponent as TeamsIcon } from '../../../assets/16x16/teams.svg';
import { ReactComponent as EmailIcon } from '../../../assets/16x16/email.svg';
import { ReactComponent as JiraIcon } from '../../../assets/16x16/jira.svg';
import { ReactComponent as FivetranIcon } from '../../../assets/fivetran-icon.svg';
import { ReactComponent as DbtCloudIcon } from '../../../assets/orchestrators/dbt-cloud-icon.svg';
import { CHAT_INTEGRATIONS } from '../../../constants/queryKeys';
import { fetchChatIntegrations } from '../../../services/api';
import { Flex } from '../../Flex';
import IconButton from '../../IconButton';
import InfoTooltip from '../../InfoTooltip';
import Switch from '../../Switch';
import { ChatIntegration } from '../types';

type IntegrationMeta = { label: string; icon: ReactElement };

const INTEGRATION_META: Record<
  Exclude<ChatIntegration, 'repo_skills'>,
  IntegrationMeta
> = {
  slack: { label: 'Slack', icon: <SlackIcon width={18} height={18} /> },
  email: { label: 'Mail', icon: <EmailIcon width={18} height={18} /> },
  teams: { label: 'Teams', icon: <TeamsIcon width={18} height={18} /> },
  jira: { label: 'Jira', icon: <JiraIcon width={18} height={18} /> },
  fivetran: {
    label: 'Fivetran',
    icon: <FivetranIcon width={18} height={18} />,
  },
  git: {
    label: 'Git',
    icon: (
      <AccountTreeRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
    ),
  },
  dbt_cloud: {
    label: 'dbt Cloud',
    icon: <DbtCloudIcon width={18} height={18} />,
  },
};

type Props = {
  enabledIntegrations?: ChatIntegration[];
  onChange?: (values: ChatIntegration[]) => void;
};

const IntegrationsPickerButton = ({ enabledIntegrations, onChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { data } = useQuery([CHAT_INTEGRATIONS], fetchChatIntegrations);

  const available = useMemo(
    () =>
      (data?.integrations ?? []).filter(
        (key): key is ChatIntegration =>
          key in INTEGRATION_META || key === 'repo_skills',
      ),
    [data],
  );

  const connected = useMemo(
    () =>
      available.filter(
        (key): key is Exclude<ChatIntegration, 'repo_skills'> =>
          key in INTEGRATION_META,
      ),
    [available],
  );

  const repoSkillsAvailable = available.includes('repo_skills');

  const selected = enabledIntegrations ?? available;
  const gitEnabled = selected.includes('git');

  const toggle = (key: ChatIntegration) => {
    let next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key];
    // Repo skills require Git — turning Git off forces repo skills off too.
    if (key === 'git' && !next.includes('git')) {
      next = next.filter((k) => k !== 'repo_skills');
    }
    onChange?.(next);
  };

  if (connected.length === 0 && !repoSkillsAvailable) return null;

  return (
    <>
      <Tooltip title="Manage integrations">
        <IconButton
          size="small"
          sx={{ padding: '4px' }}
          aria-label="Manage integrations"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <AddRoundedIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { borderRadius: 2, p: 1, minWidth: 240 } },
        }}
      >
        <Typography
          sx={{ fontSize: 12, color: 'text.secondary', px: 1.5, py: 0.5 }}
        >
          Connections
        </Typography>
        {connected.map((key) => (
          <Flex
            key={key}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 0.75,
              gap: 2,
            }}
          >
            <Flex sx={{ alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: 18 }}>
                {INTEGRATION_META[key].icon}
              </Box>
              <Typography sx={{ fontSize: 14 }}>
                {INTEGRATION_META[key].label}
              </Typography>
            </Flex>
            <Switch
              checked={selected.includes(key)}
              onChange={() => toggle(key)}
            />
          </Flex>
        ))}
        {repoSkillsAvailable && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.75,
                gap: 2,
              }}
            >
              <Flex sx={{ alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 14 }}>Use repo skills</Typography>
                <InfoTooltip
                  size={16}
                  title="Let the agent use skills defined in your connected repositories (.claude/skills and .agents/skills). Requires Git to be enabled."
                />
              </Flex>
              <Switch
                checked={gitEnabled && selected.includes('repo_skills')}
                disabled={!gitEnabled}
                onChange={() => toggle('repo_skills')}
              />
            </Flex>
          </>
        )}
      </Popover>
    </>
  );
};

export default IntegrationsPickerButton;
